import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useProduct } from '../hooks/useProducts';
import { useCart } from '../context/CartContext';
import { useIsMobile } from '../hooks/useIsMobile';
import ProductCard from '../components/ProductCard';

const H_PAD = 'clamp(60px, 12.5vw, 240px)';

export default function ProductDetail() {
  const { slug } = useParams();
  const { product, loading, related } = useProduct(slug);
  const { addItem } = useCart();
  const isMobile = useIsMobile();
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const [added, setAdded] = useState(false);
  const [selectedSize, setSelectedSize] = useState(null);

  useEffect(() => {
    setActiveImg(0);
    setQty(1);
    setSelectedSize(null);
    setAdded(false);
  }, [slug]);

  if (loading) return (
    <div style={{ padding: '120px', textAlign: 'center', fontFamily: "'Josefin Sans', sans-serif", fontSize: '12px', letterSpacing: '2px', color: '#888' }}>Chargement...</div>
  );

  if (!product) return (
    <div style={{ padding: '120px', textAlign: 'center' }}>
      <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '22px' }}>Produit introuvable.</p>
      <Link to="/catalogue" style={{ display: 'inline-block', marginTop: '20px', fontFamily: "'Josefin Sans', sans-serif", fontSize: '11px', letterSpacing: '2px', color: '#111' }}>← Retour au catalogue</Link>
    </div>
  );

  const images = product.images?.length ? product.images : ['https://images.unsplash.com/photo-1611591437281-460bfbe1220a?w=800&q=80'];
  const hasSizes = product.category === 'Bagues' && product.sizes?.length > 0;
  const availableStock = hasSizes
    ? (selectedSize ? (product.size_stock?.[selectedSize] ?? 0) : null)
    : (product.stock_quantity ?? 0);
  const outOfStock = availableStock === 0;

  const handleAdd = () => {
    if (hasSizes && !selectedSize) return alert('Veuillez sélectionner une taille.');
    if (outOfStock) return;
    addItem(product, qty, selectedSize);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  const pad = isMobile ? '40px 20px' : `clamp(40px, 4vw, 60px) ${H_PAD}`;

  return (
    <main>
      <section style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? '32px' : '60px', padding: pad }}>
        {/* Images */}
        <div>
          <div style={{ aspectRatio: '1/1', overflow: 'hidden', marginBottom: '12px', background: '#f5f5f5' }}>
            <img src={images[activeImg]} alt={product.name} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          </div>
          {images.length > 1 && (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {images.map((img, i) => (
                <div key={i} onClick={() => setActiveImg(i)} style={{
                  width: isMobile ? '60px' : '80px', height: isMobile ? '60px' : '80px',
                  overflow: 'hidden', cursor: 'pointer',
                  border: activeImg === i ? '2px solid #111' : '2px solid transparent',
                  opacity: activeImg === i ? 1 : 0.6, transition: 'all 0.2s',
                }}>
                  <img src={img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div style={{ paddingTop: isMobile ? '0' : '20px' }}>
          <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: isMobile ? '28px' : '36px', fontWeight: 400, marginBottom: '16px', letterSpacing: '1px' }}>{product.name}</h1>
          <p style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '14px', letterSpacing: '1px', color: '#555', marginBottom: '32px' }}>
            Dh {Number(product.price).toFixed(2)} MAD
          </p>

          {/* Size selector */}
          {hasSizes && (
            <div style={{ marginBottom: '28px' }}>
              <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '10px', letterSpacing: '2px', textTransform: 'uppercase', color: '#888', marginBottom: '12px' }}>
                Taille {selectedSize && <span style={{ color: '#111' }}>— {selectedSize}</span>}
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {product.sizes.map(sz => {
                  const szStock = product.size_stock?.[sz] ?? 0;
                  const szOut = szStock === 0;
                  return (
                    <button key={sz} onClick={() => { if (!szOut) { setSelectedSize(sz); setQty(1); } }} style={{
                      width: '48px', height: '48px',
                      border: `1px solid ${selectedSize === sz ? '#111' : '#ddd'}`,
                      background: selectedSize === sz ? '#111' : '#fff',
                      color: selectedSize === sz ? '#fff' : szOut ? '#ccc' : '#111',
                      fontFamily: "'Josefin Sans', sans-serif", fontSize: '12px', letterSpacing: '1px',
                      cursor: szOut ? 'not-allowed' : 'pointer', transition: 'all 0.2s',
                      textDecoration: szOut ? 'line-through' : 'none',
                    }}>{sz}</button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Stock */}
          {availableStock !== null && (
            <div style={{ marginBottom: '24px' }}>
              {outOfStock ? (
                <span style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '10px', letterSpacing: '2px', textTransform: 'uppercase', color: '#c0392b' }}>Épuisé</span>
              ) : (
                <span style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '10px', letterSpacing: '2px', textTransform: 'uppercase', color: '#888' }}>{availableStock} en stock</span>
              )}
            </div>
          )}

          {/* Quantity */}
          {availableStock !== null && !outOfStock && (
            <div style={{ marginBottom: '20px' }}>
              <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '10px', letterSpacing: '2px', textTransform: 'uppercase', color: '#888', marginBottom: '10px' }}>Quantité</div>
              <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #ddd', width: 'fit-content' }}>
                <button onClick={() => setQty(q => Math.max(1, q - 1))} style={{ width: '40px', height: '40px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: '#111' }}>−</button>
                <span style={{ width: '40px', textAlign: 'center', fontFamily: "'Josefin Sans', sans-serif", fontSize: '13px' }}>{qty}</span>
                <button onClick={() => setQty(q => Math.min(availableStock, q + 1))} style={{ width: '40px', height: '40px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: '#111' }}>+</button>
              </div>
            </div>
          )}

          {/* Buttons */}
          <button onClick={handleAdd} disabled={outOfStock} style={{
            width: '100%', padding: '16px', marginBottom: '12px', border: '1px solid #111',
            background: outOfStock ? '#f0f0f0' : added ? '#111' : '#fff',
            color: outOfStock ? '#aaa' : added ? '#fff' : '#111',
            fontFamily: "'Josefin Sans', sans-serif", fontSize: '11px', letterSpacing: '3px', textTransform: 'uppercase',
            cursor: outOfStock ? 'not-allowed' : 'pointer', transition: 'all 0.3s',
          }}>
            {outOfStock ? 'Épuisé' : added ? 'Ajouté ✓' : 'Ajouter au panier'}
          </button>
          {!outOfStock && (
            <Link to="/panier" onClick={() => { if (hasSizes && !selectedSize) return alert('Veuillez sélectionner une taille.'); addItem(product, qty, selectedSize); }} style={{
              display: 'block', width: '100%', padding: '16px', textAlign: 'center',
              background: '#111', color: '#fff', textDecoration: 'none',
              fontFamily: "'Josefin Sans', sans-serif", fontSize: '11px', letterSpacing: '3px', textTransform: 'uppercase',
            }}>Acheter maintenant</Link>
          )}

          {/* Description */}
          {product.description && (
            <div style={{ marginTop: '40px', borderTop: '1px solid #eee', paddingTop: '32px' }}>
              <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '17px', lineHeight: '1.9', color: '#444', whiteSpace: 'pre-line' }}>
                {product.description}
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section style={{ padding: isMobile ? '0 20px 60px' : `0 ${H_PAD} clamp(40px, 5vw, 80px)` }}>
          <h2 style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '18px', letterSpacing: '4px', textTransform: 'uppercase', fontWeight: 300, marginBottom: '32px', color: '#111' }}>
            Ce que vous pouvez aimer
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)', gap: isMobile ? '16px' : '32px' }}>
            {related.map(p => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}
    </main>
  );
}
